import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { 
  FaThLarge, FaBook, FaDesktop, FaUsers, FaLifeRing, 
  FaSearch, FaBell, FaTrophy, FaGamepad, FaFire, 
  FaCalendarAlt, FaMoon, FaSun, FaUserCircle, FaChevronRight,
  FaRobot, FaTicketAlt, FaServer, FaCogs, FaCog, FaSignOutAlt, FaUser, FaCreditCard, FaStar
} from 'react-icons/fa';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import './AdminPanel.css';

import { ref, onValue, set, update } from 'firebase/database';
import { db } from '../firebase';

function AdminPanel({ currentUser }) {
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [theme, setTheme] = useState("dark");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Real data state
  const [usersList, setUsersList] = useState({});
  const [activeSessions, setActiveSessions] = useState({});
  const [ticketsList, setTicketsList] = useState({});
  const [aiConfig, setAiConfig] = useState({ model: 'gemma-4-31b-it', temperature: 0.7, systemInstruction: 'You are Sampark AI...' });
  const [aiRequests, setAiRequests] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const adminRef = ref(db, `users/${currentUser.uid}/isAdmin`);
    const unsubAdmin = onValue(adminRef, (snapshot) => {
      setIsAdmin(!!snapshot.val());
      setLoading(false);
    });

    // Fetch Users
    const usersRef = ref(db, 'users');
    const unsubUsers = onValue(usersRef, (snap) => setUsersList(snap.val() || {}));

    // Fetch Status (Active Sessions)
    const statusRef = ref(db, 'status');
    const unsubStatus = onValue(statusRef, (snap) => {
      const data = snap.val() || {};
      const onlineUsers = {};
      Object.keys(data).forEach(uid => {
        if (data[uid].state === 'online') onlineUsers[uid] = data[uid];
      });
      setActiveSessions(onlineUsers);
    });

    // Fetch Tickets
    const ticketsRef = ref(db, 'tickets');
    const unsubTickets = onValue(ticketsRef, (snap) => setTicketsList(snap.val() || {}));

    // Fetch AI Config
    const aiConfigRef = ref(db, 'admin/ai_config');
    const unsubAi = onValue(aiConfigRef, (snap) => {
      if (snap.val()) setAiConfig(snap.val());
    });

    return () => {
      unsubAdmin();
      unsubUsers();
      unsubStatus();
      unsubTickets();
      unsubAi();
    };
  }, [currentUser]);

  if (loading) return null;
  if (!isAdmin) return <Navigate to="/chat" />;

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
  };

  const handleLogout = () => {
    signOut(auth);
  };

  // --- SUB-RENDERS FOR TABS ---

  const renderDashboard = () => (
    <>
      <div className="admin-page-title">
        <h1>Global Dashboard</h1>
        <p>Global dashboard reflecting platform stats across Bharat Connect.</p>
      </div>

      <div className="admin-tabs">
        <button className="admin-tab active">Summary</button>
        <button className="admin-tab">Metrics</button>
        <button className="admin-tab">AI Tokens</button>
      </div>

      {/* STATS GRID */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-header">
            <div className="stat-title"><FaTrophy className="stat-icon" /> Active Sessions</div>
            <FaChevronRight className="stat-arrow" />
          </div>
          <div className="stat-body">
            <h2>{Object.keys(activeSessions).length}</h2>
            <span className="stat-label neutral">Stable</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-header">
            <div className="stat-title"><FaGamepad className="stat-icon" /> Total Users</div>
            <FaChevronRight className="stat-arrow" />
          </div>
          <div className="stat-body">
            <h2>{Object.keys(usersList).length}</h2>
            <span className="stat-label positive">Total registered</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-header">
            <div className="stat-title"><FaFire className="stat-icon" /> AI Requests (24h)</div>
          </div>
          <div className="stat-body">
            <h2>{aiRequests}</h2>
            <span className="stat-label positive">reqs</span>
          </div>
        </div>

        <div className="admin-stat-card highlight">
          <div className="stat-header">
            <div className="stat-title"><FaTicketAlt className="stat-icon" /> Open Tickets</div>
          </div>
          <div className="stat-body">
            <h2 className={Object.keys(ticketsList).length > 0 ? "coming-soon" : ""}>
              {Object.keys(ticketsList).filter(id => ticketsList[id].status !== 'closed').length} Pending
            </h2>
            <span className="stat-label neutral">Needs review</span>
          </div>
        </div>
      </div>

      {/* CHARTS SECTION */}
      <div className="admin-charts-section">
        <div className="admin-chart-main">
          <div className="chart-header">
            <h3>Platform Traffic & AI Usage</h3>
            <div className="chart-actions">
              <span className="chart-badge bg-blue"><FaServer /> Node: Stable</span>
              <span className="chart-badge bg-purple"><FaCogs /> AI: Active</span>
              <select className="chart-select">
                <option>This Week</option>
              </select>
            </div>
          </div>
          <div className="chart-placeholder">
            <h4>Nothing to show here</h4>
            <p>There is no data to show here yet, either because traffic is low or logs are still syncing.</p>
            <div className="chart-bars-bg"></div>
          </div>
        </div>

        <div className="admin-chart-side">
          <h3>System Schedule</h3>
          <p>Maintenance and deployments waiting for you</p>
          <div className="calendar-card">
            <div className="calendar-header">
              <button>{'<'}</button>
              <span>June 2026</span>
              <button>{'>'}</button>
            </div>
            <div className="calendar-grid">
              {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d} className="cal-day-name">{d}</div>)}
              {/* Dummy Days */}
              {[30, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27].map((d, i) => (
                <div key={i} className={`cal-day ${d === 16 ? 'active' : ''}`}>{d}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderAIConfig = () => (
    <div className="admin-subpage">
      <div className="admin-page-title">
        <h1>AI Configuration</h1>
        <p>Manage Sampark AI's core behavior.</p>
      </div>
      <div className="admin-card">
        <label>Active Model</label>
        <select value={aiConfig.model} onChange={(e) => setAiConfig({...aiConfig, model: e.target.value})} className="admin-input">
          <option value="gemma-4-31b-it">Gemma 4 31B IT</option>
          <option value="gemini-3.1-flash-lite">Gemini 3.1 Flash Lite</option>
          <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
        </select>
        
        <label>Temperature</label>
        <input type="number" step="0.1" value={aiConfig.temperature} onChange={(e) => setAiConfig({...aiConfig, temperature: parseFloat(e.target.value)})} className="admin-input" />

        <label>System Instruction</label>
        <textarea rows="6" value={aiConfig.systemInstruction} onChange={(e) => setAiConfig({...aiConfig, systemInstruction: e.target.value})} className="admin-input" />
        
        <button className="upgrade-btn mt-3" onClick={() => {
          set(ref(db, 'admin/ai_config'), aiConfig);
          alert('AI Config saved to RTDB!');
        }}>Save Configuration</button>
      </div>
    </div>
  );

  const renderSessions = () => (
    <div className="admin-subpage">
      <div className="admin-page-title">
        <h1>Active Sessions</h1>
        <p>Currently online users.</p>
      </div>
      <div className="admin-card">
        <table className="admin-table">
          <thead><tr><th>User ID</th><th>Last Active</th><th>Status</th></tr></thead>
          <tbody>
            {Object.keys(activeSessions).length === 0 ? (
              <tr><td colSpan="3" style={{textAlign: 'center', padding: '20px'}}>No active sessions</td></tr>
            ) : (
              Object.keys(activeSessions).map(uid => (
                <tr key={uid}>
                  <td>{uid}</td>
                  <td>{new Date(activeSessions[uid].last_changed).toLocaleString()}</td>
                  <td><span className="stat-label positive">Online</span></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="admin-subpage">
      <div className="admin-page-title">
        <h1>User Management</h1>
        <p>Manage registered accounts and admin privileges.</p>
      </div>
      <div className="admin-card">
        <table className="admin-table">
          <thead><tr><th>Email/UID</th><th>Phone Verified</th><th>Role</th><th>Actions</th></tr></thead>
          <tbody>
            {Object.keys(usersList).map(uid => (
              <tr key={uid}>
                <td>{uid}</td>
                <td>{usersList[uid].isPhoneVerified ? '✅ Yes' : '❌ No'}</td>
                <td>{usersList[uid].isAdmin ? 'Admin' : 'User'}</td>
                <td>
                  {!usersList[uid].isAdmin ? (
                    <button className="admin-btn-small" onClick={() => update(ref(db, `users/${uid}`), {isAdmin: true})}>Promote</button>
                  ) : (
                    <button className="admin-btn-small danger" onClick={() => update(ref(db, `users/${uid}`), {isAdmin: false})}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTickets = () => (
    <div className="admin-subpage">
      <div className="admin-page-title">
        <h1>Support Tickets</h1>
        <p>Manage user-submitted issues.</p>
      </div>
      <div className="admin-card">
        <table className="admin-table">
          <thead><tr><th>Ticket ID</th><th>Issue</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {Object.keys(ticketsList).length === 0 ? (
              <tr><td colSpan="4" style={{textAlign: 'center', padding: '20px'}}>No tickets</td></tr>
            ) : (
              Object.keys(ticketsList).map(tid => (
                <tr key={tid}>
                  <td>{tid}</td>
                  <td>{ticketsList[tid].issue}</td>
                  <td>{ticketsList[tid].status}</td>
                  <td>
                    {ticketsList[tid].status !== 'closed' && (
                      <button className="admin-btn-small" onClick={() => update(ref(db, `tickets/${tid}`), {status: 'closed'})}>Close</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );


  return (
    <div className={`admin-dashboard-container ${theme}`} onClick={() => setShowProfileMenu(false)}>
      {/* SIDEBAR */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-logo">
            <div className="admin-logo-icon">BC</div>
            <h2>Bharat Connect</h2>
          </div>
        </div>

        <nav className="admin-nav">
          <div className={`admin-nav-item ${activeTab === 'Dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('Dashboard')}>
            <FaThLarge className="admin-nav-icon" /> Dashboard
          </div>
          <div className={`admin-nav-item ${activeTab === 'AI Config' ? 'active' : ''}`} onClick={() => setActiveTab('AI Config')}>
            <FaRobot className="admin-nav-icon" /> AI Config <FaChevronRight className="admin-nav-arrow" />
          </div>
          <div className={`admin-nav-item ${activeTab === 'Sessions' ? 'active' : ''}`} onClick={() => setActiveTab('Sessions')}>
            <FaDesktop className="admin-nav-icon" /> Sessions <FaChevronRight className="admin-nav-arrow" />
          </div>
          <div className={`admin-nav-item ${activeTab === 'Users' ? 'active' : ''}`} onClick={() => setActiveTab('Users')}>
            <FaUsers className="admin-nav-icon" /> Users <FaChevronRight className="admin-nav-arrow" />
          </div>
          <div className={`admin-nav-item ${activeTab === 'Tickets' ? 'active' : ''}`} onClick={() => setActiveTab('Tickets')}>
            <FaTicketAlt className="admin-nav-icon" /> Tickets <FaChevronRight className="admin-nav-arrow" />
          </div>
        </nav>

        <div className="admin-sidebar-bottom">
          {/* Upgrade Card Mock */}
          <div className="admin-upgrade-card">
            <div className="upgrade-icon"><FaServer /></div>
            <h4>System Status: Optimal</h4>
            <p>All AI endpoints and real-time nodes are operational.</p>
            <button className="upgrade-btn">View Logs</button>
          </div>

          <div className="admin-theme-toggle" onClick={toggleTheme}>
            {theme === "dark" ? <FaMoon className="theme-icon" /> : <FaSun className="theme-icon" />}
            <span>Night Mode</span>
            <span className="shortcut">Ctrl+M</span>
          </div>

          <div className="admin-user-profile-container" onClick={(e) => { e.stopPropagation(); setShowProfileMenu(!showProfileMenu); }}>
            <div className="admin-user-profile">
              <div className="admin-user-avatar">
                {currentUser?.email ? currentUser.email.substring(0, 2).toUpperCase() : 'AD'}
              </div>
              <div className="admin-user-info">
                <span className="admin-user-name">System Admin</span>
                <span className="admin-user-email">{currentUser?.email || "admin@bharatconnect"}</span>
              </div>
              <FaChevronRight className="admin-nav-arrow" />
            </div>

            {/* PROFILE DROPDOWN MENU */}
            {showProfileMenu && (
              <div className="admin-profile-menu">
                <div className="menu-header">
                  <div className="admin-user-avatar">
                    {currentUser?.email ? currentUser.email.substring(0, 2).toUpperCase() : 'AD'}
                  </div>
                  <div className="admin-user-info">
                    <span className="admin-user-name">System Admin</span>
                    <span className="admin-user-email">{currentUser?.email || "admin@bharatconnect"}</span>
                  </div>
                </div>
                <div className="menu-divider"></div>
                <div className="menu-item">
                  <FaStar className="menu-icon" /> Upgrade to Pro
                </div>
                <div className="menu-divider"></div>
                <div className="menu-item">
                  <FaUser className="menu-icon" /> Profile
                </div>
                <div className="menu-item">
                  <FaCog className="menu-icon" /> Account
                </div>
                <div className="menu-item">
                  <FaCreditCard className="menu-icon" /> Billing
                </div>
                <div className="menu-item">
                  <FaDesktop className="menu-icon" /> Sessions
                </div>
                <div className="menu-item">
                  <FaBell className="menu-icon" /> Notifications
                </div>
                <div className="menu-divider"></div>
                <div className="menu-item logout" onClick={handleLogout}>
                  <FaSignOutAlt className="menu-icon" /> Logout
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="admin-main-content">
        {/* HEADER */}
        <header className="admin-header">
          <div className="admin-breadcrumbs">
            <FaBook className="breadcrumb-icon"/> Home <span className="separator">{'>'}</span> <span className="current">{activeTab}</span>
          </div>
          <div className="admin-header-actions">
            <div className="admin-search-bar">
              <FaSearch className="search-icon" />
              <input type="text" placeholder="Search" />
              <div className="search-shortcut"><span>Ctrl</span> K</div>
            </div>
            <button className="admin-icon-btn"><FaBell /></button>
            <button className="admin-live-btn"><FaServer className="live-icon" /> Live Dashboard</button>
          </div>
        </header>

        {/* DASHBOARD BODY */}
        <div className="admin-dashboard-body">
          {activeTab === 'Dashboard' && renderDashboard()}
          {activeTab === 'AI Config' && renderAIConfig()}
          {activeTab === 'Sessions' && renderSessions()}
          {activeTab === 'Users' && renderUsers()}
          {activeTab === 'Tickets' && renderTickets()}
        </div>
      </main>
    </div>
  );
}

export default AdminPanel;
